import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { headCells, STORAGE_KEY_DENSE, STORAGE_KEY_RPP } from "./constants";
import ApiService from "../../../modules/APIService.interceptors";
import MembershipModal from "./_parts/MembershipModal";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableContainer,
  TablePagination,
} from "@mui/material";
import UserRows from "./_parts/UserRows";
import TableHeaderRow from "./_parts/TableHeaderRow";
import SelectionToolbar from "./_parts/SelectionToolbar";
import TopBar from "./_parts/TopBar";
import { getComparator } from "./utils";
import PaymentsService from "../../../services/payment.service";
import { useNotifier } from "../../../hooks/useNotifier";
import UserService from "../../../services/user.service";

export default function UserTable() {
  const navigate = useNavigate();
  const notify = useNotifier();

  // API & UI state
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loader, setLoader] = useState(false);

  // Table state (server paginated)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Sort + selection + density
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("fullName");
  const [selected, setSelected] = useState([]);
  const [dense, setDense] = useState(false);

  // Search/filters
  const headersFilters = [
    { label: "Nombre Apellido", value: "name" },
    { label: "Email", value: "email" },
    { label: "Instagram", value: "instagram" },
    { label: "Teléfono", value: "phone" },
    { label: "DNI", value: "dni" },
    { label: "Alias", value: "alias" },
  ];
  const [filter, setFilter] = useState("name");
  const [searchText, setSearchText] = useState("");

  // Membership filter
  const [membershipFilter, setMembershipFilter] = useState("all");
  const [allMemberships, setAllMemberships] = useState([]);

  // Modal membresía
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);

  // Cargar todas las membresías al inicio
  useEffect(() => {
    const loadMemberships = async () => {
      try {
        const memberships = await UserService.getAllMemberships();
        setAllMemberships(memberships || []);
      } catch (error) {
        console.error("Error al cargar membresías:", error);
      }
    };
    loadMemberships();
  }, []);

  // Persisted prefs
  useEffect(() => {
    try {
      const savedDense = localStorage.getItem(STORAGE_KEY_DENSE);
      if (savedDense === "1") setDense(true);
      if (savedDense === "0") setDense(false);

      const savedRpp = localStorage.getItem(STORAGE_KEY_RPP);
      if (savedRpp) {
        const num = parseInt(savedRpp, 10);
        if (!Number.isNaN(num)) {
          setRowsPerPage(num);
          fetchUsers(0, num);
          return;
        }
      }
      fetchUsers(0);
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (
    newPage = page,
    currentRowsPerPage = rowsPerPage,
    currentSearch = searchText,
    currentMembershipFilter = membershipFilter
  ) => {
    try {
      setLoader(true);
      const safeFilter =
        filter && String(filter).trim() ? filter.trim() : "name";
      const rawSearch = currentSearch ?? "";
      const response = await ApiService.getUsers(
        currentRowsPerPage,
        newPage,
        safeFilter,
        rawSearch,
        currentMembershipFilter
      );

      console.log("Respuesta API usuarios:", response);
      if (!response?.error) {
        setUsers(response.data?.users || []);
        setTotalUsers(response?.total || 0);
      } else {
        console.error("Error al obtener usuarios");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  const handleRequestSort = (event, property) => {
    if (!headCells.find((h) => h.id === property && h.sortable)) return;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = (users || []).map((u) => u.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleRowClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let next = [];
    if (selectedIndex === -1) next = next.concat(selected, id);
    else if (selectedIndex === 0) next = next.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1)
      next = next.concat(selected.slice(0, -1));
    else if (selectedIndex > 0)
      next = next.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    setSelected(next);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSelected([]);
    fetchUsers(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    try {
      localStorage.setItem(STORAGE_KEY_RPP, String(value));
    } catch { }
    setPage(0);
    setSelected([]);
    fetchUsers(0, value);
  };

  const handleChangeDense = (event) => {
    const next = event.target.checked;
    setDense(next);
    try {
      localStorage.setItem(STORAGE_KEY_DENSE, next ? "1" : "0");
    } catch { }
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchText(value);
    setPage(0);
    fetchUsers(0, rowsPerPage, value);
  };

  const handleMembershipFilterChange = (event) => {
    const { value } = event.target;
    setMembershipFilter(value);
    setPage(0);
    fetchUsers(0, rowsPerPage, searchText, value);
  };

  const sortedUsers = useMemo(() => {
    const sortable = headCells.find((h) => h.id === orderBy && h.sortable);
    const list = Array.isArray(users) ? users.slice() : [];
    return sortable ? list.sort(getComparator(order, orderBy)) : list;
  }, [users, order, orderBy]);

  const goToProfile = (id) => navigate(`/usuarios/${id}`);

  // Actualización local post-asignación
  const markUsersAsActive = (userIds, membershipId, pack) => {
    setUsers((prev) =>
      prev.map((u) => {
        if (!userIds.includes(u.id)) return u;
        const current = Array.isArray(u.membership) ? u.membership : [];
        if (current.some((m) => Number(m?.id) === Number(membershipId)))
          return u;
        return {
          ...u,
          membership: [
            ...current,
            { id: membershipId, end: pack?.membership?.end ?? null },
          ],
        };
      })
    );
  };

  const handleConfirmMembership = async (selectedPack) => {
    if (!selectedPack || selected.length === 0) return;
    const productId = selectedPack?.product?.ARG?.id;
    const membershipId = selectedPack?.membership?.id;
    if (!productId || !membershipId) return;

    try {
      const resp = await PaymentsService.createPaymentManual({
        userIds: selected,
        productId,
      });

      let successIds = selected;
      if (resp?.data?.success && Array.isArray(resp.data.success)) {
        successIds = resp.data.success.map((s) => s.userId).filter(Boolean);
      }

      markUsersAsActive(successIds, membershipId, selectedPack);
      setSelected([]);
      setMembershipModalOpen(false);

      if (successIds.length > 0) {
        notify(
          `Membresía asignada a ${successIds.length} usuaria(s).`,
          "success"
        );
      } else {
        notify(
          "No se pudo asignar la membresía a las usuarias seleccionadas.",
          "warning"
        );
      }
    } catch (e) {
      console.error(e);
      notify("No se pudo asignar la membresía.", "error");
    }
  };

  return (
    <TableContainer component={Paper}>
      <TopBar
        selectedCount={selected.length}
        onOpenModal={() => setMembershipModalOpen(true)}
        searchText={searchText}
        setSearchText={setSearchText}
        headersFilters={headersFilters}
        filter={filter}
        setFilter={setFilter}
        onSearchChange={handleSearchChange}
        membershipFilter={membershipFilter}
        onMembershipFilterChange={handleMembershipFilterChange}
        allMemberships={allMemberships}
      />

      {loader ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>
          <SelectionToolbar numSelected={selected.length} />

          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <TableHeaderRow
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={sortedUsers.length}
            />
            <UserRows
              rows={sortedUsers}
              isSelected={isSelected}
              onRowClick={handleRowClick}
              goToProfile={goToProfile}
            />
          </Table>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={dense}
                  onChange={handleChangeDense}
                  color="secondary"
                />
              }
              label="Compacta"
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
            />
          </Box>
        </Box>
      )}

      <MembershipModal
        open={membershipModalOpen}
        onClose={() => setMembershipModalOpen(false)}
        onConfirm={handleConfirmMembership}
      />
    </TableContainer>
  );
}
